import React, { useState } from "react";
import PropTypes from "prop-types";

import LabTestSearch from "./LabTestSearch";
import { Close, Chevron } from "./RxIcons";

/**
 * Card · Advised investigations — Figma 3031:9725.
 *
 * The one card that is NOT a picker: there is nothing to tick, because a test
 * being advised is created here rather than chosen from the patient's record.
 * So it can't reuse RxPickerCard (checkbox rows) — the body is a repeatable
 * two-field form: the test name, an editable instruction, and a remove ×.
 *
 * Behaviour follows bridge's "Advised Investigation" card: search the lab-test
 * catalogue, the picked test's default advice prefills the instruction, and the
 * doctor edits it. The one difference is where the search lives — bridge adds
 * an empty row with a "+" and searches inside it, phr's design puts the search
 * in the header and a pick adds the row. The payload is identical either way.
 *
 * The test name is READ-ONLY on purpose: it has to keep matching its LABT code,
 * and bridge's free-typed name (which never reaches the payload unless a
 * suggestion is picked) is a trap worth not copying.
 */

const AdvisedInvestigationsCard = ({ rows, onAdd, onChangeInstruction, onRemove }) => {
  const [open, setOpen] = useState(true);

  return (
    <section className="rx-card rx-card-split">
      <header className="rx-card-head is-tall">
        <h3 className="rx-card-title">Advised investigations</h3>
        <div className="rx-card-head-end">
          <LabTestSearch onAdd={onAdd} onDefaultInstruction={onChangeInstruction} />
          <button
            type="button"
            className={`rx-collapse${open ? "" : " is-collapsed"}`}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} advised investigations`}
            onClick={() => setOpen((o) => !o)}
          >
            <Chevron />
          </button>
        </div>
      </header>

      {open && (
        <div className="rx-inv-body">
          {rows.length === 0 ? (
            // Not in the design — the board only draws the filled state. Kept
            // deliberately quiet: the search above it is the whole affordance.
            <p className="rx-inv-empty">
              No investigations advised. Search above to add one.
            </p>
          ) : (
            rows.map((row) => (
              <div className="rx-inv-row" key={row.id}>
                <div className="rx-field">
                  <span className="rx-inv-label">Investigation</span>
                  <input
                    className="rx-inv-input"
                    type="text"
                    value={row.labTest.label}
                    readOnly
                    aria-label="Investigation"
                  />
                </div>
                <div className="rx-field">
                  <span className="rx-inv-label">Instructions</span>
                  <input
                    className="rx-inv-input"
                    type="text"
                    value={row.instruction}
                    onChange={(e) => onChangeInstruction(row.id, e.target.value)}
                    aria-label={`Instructions for ${row.labTest.label}`}
                  />
                </div>
                <button
                  type="button"
                  className="rx-inv-x"
                  aria-label={`Remove ${row.labTest.label}`}
                  onClick={() => onRemove(row.id)}
                >
                  <Close />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
};

AdvisedInvestigationsCard.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      labTest: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }).isRequired,
      instruction: PropTypes.string,
    })
  ).isRequired,
  /** `([{label, value}, …])` — every row ticked in the popover. */
  onAdd: PropTypes.func.isRequired,
  /** `(idOrCode, text)` — edits an instruction; also the default-advice sink. */
  onChangeInstruction: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default AdvisedInvestigationsCard;
